import { useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';

import { sendFEEvent } from '../../../eventUtil';
import { useOrderPolling } from '../../Checkout/hooks/use-order-polling';
import { usePaymentState } from '../../Checkout/hooks/use-payment-state';
import { useAPI } from '../../../services/api.service';
import { useCheckout } from '../../../hooks/use-checkout';
import { ECheckoutStatus } from '../../../constants/enums';
import {
  ESdkEvents,
  EPaymentProvider,
  EPaymentMethodDisplayName,
  PaypalFinalizePaymentResponse,
  AppchargeErrorType,
} from '../../../contracts';
import { createPaypalFinalizePaymentBody, PaypalSubmitParams } from '../utils';
import { generateAppchargeErrorMessage } from '../../../utils/generate-appcharge-error-message';

export function usePaypalSubmit() {
  const { API } = useAPI();
  const isPaypalPaymentInProgress = useRef(false);
  const { showBoundary } = useErrorBoundary();
  const { getEventParams, selectedPaymentMethodDisplayNameRef } = useCheckout();
  const { startOrderPolling } = useOrderPolling(handleError);
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setCheckoutStatus = usePaymentState((state) => state.setCheckoutStatus);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });

  async function handleSubmit(submitParams: PaypalSubmitParams) {
    if (isPaypalPaymentInProgress.current) {
      return;
    }
    setErrorMessage('');
    setIsPaymentLoading(true);
    isPaypalPaymentInProgress.current = true;
    selectedPaymentMethodDisplayNameRef.current = EPaymentMethodDisplayName.paypal;
    try {
      const data = await API.finalizePayment<PaypalFinalizePaymentResponse>(
        createPaypalFinalizePaymentBody(submitParams)
      );
      handleServerResponse(data, submitParams.paymentProvider);
    } catch (error: any) {
      const errResponse = error?.response?.data;
      setIsPaymentLoading(false);
      showBoundary(t('unableToProcessPayment'));
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: errResponse.message,
        error: {
          isFatal: true,
          reason: errResponse.message,
        },
      });
    }
  }

  function handleServerResponse(
    response: PaypalFinalizePaymentResponse,
    provider: EPaymentProvider.braintree | EPaymentProvider.paypal
  ) {
    const status = response[provider]?.status;
    const message = response[provider]?.message;
    if (status === 'failed') {
      handleError(response.appChargeErrorType);
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: message,
      });
      setCheckoutStatus(ECheckoutStatus.INIT);
    } else if (status === 'succeeded') {
      setErrorMessage('');
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
        ...getEventParams(),
      });
      startOrderPolling(response.orderId);
    }
  }

  function handleError(appChargeErrorType?: AppchargeErrorType) {
    const errorMessage = generateAppchargeErrorMessage(t, appChargeErrorType);
    setErrorMessage(errorMessage);
    setIsPaymentLoading(false);
    isPaypalPaymentInProgress.current = false;
  }

  return {
    handlePaypalSubmit: handleSubmit,
  };
}
